import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isFreemiumTier } from '@shared';

import type { FreemiumIntentId, FreemiumTier } from '@shared';

export type FreemiumGateStep =
  | 'intent'
  | 'paywall'
  | 'licensedConfirm'
  | 'checkout'
  | 'licensedDone';

const STEP_PARAM = 'fxStep';
const INTENT_PARAM = 'fxIntent';
const TIER_PARAM = 'fxTier';

const VALID_STEPS: FreemiumGateStep[] = [
  'intent',
  'paywall',
  'licensedConfirm',
  'checkout',
  'licensedDone',
];

function isGateStep(value: string | null): value is FreemiumGateStep {
  return !!value && (VALID_STEPS as string[]).includes(value);
}

export function useFreemiumUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStep = searchParams.get(STEP_PARAM);
  const step: FreemiumGateStep = isGateStep(rawStep) ? rawStep : 'intent';

  const intent = (searchParams.get(INTENT_PARAM) as FreemiumIntentId | null) ?? null;

  const rawTier = searchParams.get(TIER_PARAM);
  const selectedTier: FreemiumTier | null = rawTier && isFreemiumTier(rawTier) ? rawTier : null;

  const update = useCallback(
    (updates: Record<string, string | null>, options?: { replace?: boolean }) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const setIntent = useCallback(
    (next: FreemiumIntentId) => update({ [INTENT_PARAM]: next }, { replace: true }),
    [update],
  );

  const goToPaywall = useCallback(() => update({ [STEP_PARAM]: 'paywall' }), [update]);

  const goToIntent = useCallback(() => update({ [STEP_PARAM]: 'intent' }), [update]);

  const startLicensedConfirm = useCallback(
    (tier: FreemiumTier) => update({ [STEP_PARAM]: 'licensedConfirm', [TIER_PARAM]: tier }),
    [update],
  );

  const startCheckout = useCallback(
    (tier: FreemiumTier) => update({ [STEP_PARAM]: 'checkout', [TIER_PARAM]: tier }),
    [update],
  );

  const goToLicensedDone = useCallback(() => update({ [STEP_PARAM]: 'licensedDone' }), [update]);

  return {
    step,
    intent,
    selectedTier,
    setIntent,
    goToPaywall,
    goToIntent,
    startLicensedConfirm,
    startCheckout,
    goToLicensedDone,
  };
}
